/**
 * Reads the line layout of a rendered paragraph straight from the browser.
 *
 * react-pdf's line breaker is allowed to squeeze word spaces by up to a third
 * to fit an extra word on a line, which Chrome never does, so the two engines
 * can disagree on where lines break even with identical fonts and widths. The
 * builder is therefore the source of truth: at export time every paragraph's
 * lines (text, styling and the extra word spacing Chrome applied to justify
 * the line) are measured here and DocumentPDF renders them line by line.
 *
 * The same token walk feeds pagination (`measureLineStarts`), so the editor
 * and the PDF always agree on where lines start. Forced breaks (<br>) are
 * tokens too, which is how blank lines inside a paragraph are counted.
 */

export interface LayoutRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  /** Inside a <sub>: drawn smaller and dropped (subscript.ts). */
  sub?: boolean;
  backgroundColor?: string;
  href?: string;
}

export interface LayoutLine {
  runs: LayoutRun[]; // empty for a blank line
  /** Extra advance added to every space on this line, in pt (justification). */
  wordSpacingPt: number;
}

export type ParagraphLayout = LayoutLine[];

/**
 * Line structure of a rendered paragraph, for pagination: the character offset
 * (into textContent) at which each visual line starts, plus the line height.
 * Lines are uniform within a paragraph, so line i occupies
 * [i * lineHeightPx, (i + 1) * lineHeightPx] from the top of the paragraph.
 */
export interface ParagraphLineInfo {
  lineStarts: number[];
  lineHeightPx: number;
  textLength: number;
}

const PX_TO_PT = 0.75;

type RunStyle = Omit<LayoutRun, 'text'>;

interface FontInfo { style: string; weight: string; size: string; family: string }

interface Token {
  kind: 'word' | 'space' | 'break';
  text: string;
  charStart: number; // offset into the paragraph's textContent
  /**
   * A break that is itself a character (a newline under pre-wrap) ends its
   * line AT charStart but the next line begins one character later. A <br>
   * has no length, so for it the two are the same and this is absent.
   */
  nextStart?: number;
  left: number;
  right: number;
  top: number;
  style: RunStyle;
  fontKey: string;
  font: FontInfo;
}

const rgbToHex = (value: string): string | undefined => {
  const m = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\)/);
  if (!m) return undefined;
  if (m[4] !== undefined && Number(m[4]) === 0) return undefined; // transparent
  const hex = (n: string) => Number(n).toString(16).padStart(2, '0');
  return `#${hex(m[1])}${hex(m[2])}${hex(m[3])}`;
};

const styleKey = (s: RunStyle): string =>
  [s.bold ? 'b' : '', s.italic ? 'i' : '', s.underline ? 'u' : '', s.strike ? 's' : '', s.sub ? 'sub' : '', s.backgroundColor || '', s.href || ''].join('|');

// Background colour of the nearest ancestor (up to the paragraph) that paints one
const inheritedBackground = (element: Element, root: HTMLElement, win: Window): string | undefined => {
  let current: Element | null = element;
  while (current && current !== root) {
    const bg = rgbToHex(win.getComputedStyle(current).backgroundColor);
    if (bg) return bg;
    current = current.parentElement;
  }
  return undefined;
};

const styleFor = (parent: HTMLElement, root: HTMLElement, win: Window): { style: RunStyle; font: FontInfo; fontKey: string } => {
  const cs = win.getComputedStyle(parent);
  const weight = parseInt(cs.fontWeight, 10);
  const style: RunStyle = {
    bold: (Number.isFinite(weight) ? weight >= 600 : cs.fontWeight === 'bold') || !!parent.closest('b, strong'),
    italic: cs.fontStyle === 'italic',
    underline: !!parent.closest('u'),
    strike: !!parent.closest('s, strike, del'),
    sub: !!parent.closest('sub') && root.contains(parent.closest('sub')),
    backgroundColor: inheritedBackground(parent, root, win),
    href: parent.closest('a')?.getAttribute('href') || undefined,
  };
  const font = { style: cs.fontStyle, weight: cs.fontWeight, size: cs.fontSize, family: cs.fontFamily };
  return { style, font, fontKey: `${font.style}|${font.weight}|${font.size}|${font.family}` };
};

/**
 * Words, whitespace and forced breaks of a rendered paragraph in document
 * order, each with its screen rectangle and character offset.
 */
const collectTokens = (el: HTMLElement): Token[] => {
  const doc = el.ownerDocument;
  const win = doc.defaultView;
  if (!win) return [];
  const tokens: Token[] = [];
  let acc = 0;

  const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) =>
      node.nodeType === Node.TEXT_NODE || (node as Element).tagName === 'BR'
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_SKIP,
  });

  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      // <br>: a forced line break. Its rect sits at the end of the line it ends.
      const br = node as HTMLElement;
      const parent = br.parentElement || el;
      const range = doc.createRange();
      range.selectNode(br);
      const rect = range.getClientRects()[0] || br.getBoundingClientRect();
      const meta = styleFor(parent, el, win);
      tokens.push({ kind: 'break', text: '', charStart: acc, left: rect.left, right: rect.right, top: Math.round(rect.top), ...meta });
      continue;
    }

    const textNode = node as Text;
    const parent = textNode.parentElement;
    if (!parent) continue;
    const meta = styleFor(parent, el, win);
    const text = textNode.data;
    // Under a preserving white-space (a table appendix cell is pre-wrap), a
    // newline character IS a forced break, exactly like <br>; under normal
    // white-space it collapses to a space and must stay one.
    const preserves = /^pre/.test(win.getComputedStyle(parent).whiteSpace);
    const re = preserves ? /\n|[^\S\n]+|[^\s]+/g : /\s+|\S+/g;
    let match: RegExpExecArray | null;
    let lastTop = tokens.length > 0 ? tokens[tokens.length - 1].top : 0;
    while ((match = re.exec(text)) !== null) {
      if (preserves && match[0] === '\n') {
        // Its own rect is often empty at a line's end; the break sits on the
        // line of whatever preceded it, which is all groupLines needs.
        const range = doc.createRange();
        range.setStart(textNode, match.index);
        range.setEnd(textNode, match.index + 1);
        const rect = range.getClientRects()[0];
        tokens.push({ kind: 'break', text: '', charStart: acc + match.index, nextStart: acc + match.index + 1, left: rect?.left ?? 0, right: rect?.right ?? 0, top: rect ? Math.round(rect.top) : lastTop, ...meta });
        continue;
      }
      const isSpace = /^\s+$/.test(match[0]);
      const range = doc.createRange();
      range.setStart(textNode, match.index);
      range.setEnd(textNode, match.index + match[0].length);
      const rects = Array.from(range.getClientRects());
      if (rects.length === 0) continue;
      lastTop = Math.round(rects[0].top);

      if (!isSpace && rects.length > 1) {
        // A word broken across lines (overflow-wrap): measure character by character
        for (let i = 0; i < match[0].length; i++) {
          const charRange = doc.createRange();
          charRange.setStart(textNode, match.index + i);
          charRange.setEnd(textNode, match.index + i + 1);
          const rect = charRange.getClientRects()[0];
          if (!rect) continue;
          tokens.push({ kind: 'word', text: match[0][i], charStart: acc + match.index + i, left: rect.left, right: rect.right, top: Math.round(rect.top), ...meta });
        }
        continue;
      }

      const rect = rects[0];
      tokens.push({ kind: isSpace ? 'space' : 'word', text: match[0], charStart: acc + match.index, left: rect.left, right: rect.right, top: Math.round(rect.top), ...meta });
    }
    acc += text.length;
  }
  return tokens;
};

/**
 * Group tokens into visual lines. A word whose top differs from the current
 * line starts a new line; a forced break ends the current line and starts a
 * new one at its own offset, so two breaks in a row yield an empty line.
 * Whitespace never starts a line and is trimmed from line ends.
 */
const groupLines = (tokens: Token[], lineHeightPx: number): Array<{ charStart: number; tokens: Token[] }> => {
  const lines: Array<{ charStart: number; tokens: Token[] }> = [];
  let current: { charStart: number; tokens: Token[] } | null = null;
  // The top of the line's ordinary text, and of its subscript text, as each is
  // first seen. A subscript's glyphs sit lower than the text around it (a
  // smaller font, dropped), so its rect top differs from theirs on the SAME
  // line; comparing it like any other word split "Md" into two lines and the
  // PDF drew the "d" a line below the "M".
  let currentTop: number | null = null;
  let currentSubTop: number | null = null;
  const sameLine = (token: Token): boolean => {
    const isSub = !!token.style.sub;
    const sameKind = isSub ? currentSubTop : currentTop;
    if (sameKind !== null) return token.top === sameKind;
    // Only the other kind seen so far. A subscript on this line sits below
    // its text's top but by less than a line (about half the font size); a
    // line's text sits above the subscript on it, never below.
    if (isSub && currentTop !== null) return token.top > currentTop && token.top - currentTop < lineHeightPx;
    if (!isSub && currentSubTop !== null) return token.top < currentSubTop;
    return true;
  };

  for (const token of tokens) {
    if (token.kind === 'break') {
      if (!current) current = { charStart: token.charStart, tokens: [] };
      lines.push(current);
      // The line after starts past the newline character: a page split at
      // it must not carry the newline over as a blank first line.
      current = { charStart: token.nextStart ?? token.charStart, tokens: [] };
      currentTop = null;
      currentSubTop = null;
      continue;
    }
    if (token.kind === 'space') {
      if (current && current.tokens.length > 0) current.tokens.push(token);
      continue;
    }
    if (current && current.tokens.length > 0 && !sameLine(token)) {
      lines.push(current);
      current = null;
      currentTop = null;
      currentSubTop = null;
    }
    if (!current) current = { charStart: token.charStart, tokens: [] };
    else if (current.tokens.length === 0) current.charStart = Math.min(current.charStart, token.charStart);
    if (token.style.sub) currentSubTop = token.top;
    else currentTop = token.top;
    current.tokens.push(token);
  }
  // A trailing break leaves an empty "line" the browser does not draw; drop it
  if (current && (current.tokens.length > 0 || lines.length === 0)) lines.push(current);

  for (const line of lines) {
    while (line.tokens.length > 0 && line.tokens[line.tokens.length - 1].kind === 'space') line.tokens.pop();
  }
  return lines;
};

// The paragraph's line pitch, for telling a subscript from the next line.
const lineHeightOf = (el: HTMLElement): number => {
  const win = el.ownerDocument.defaultView;
  if (!win) return 0;
  const cs = win.getComputedStyle(el);
  return parseFloat(cs.lineHeight) || (parseFloat(cs.fontSize) || 0) * 1.15;
};

export const measureLineStarts = (el: HTMLElement): ParagraphLineInfo => {
  const win = el.ownerDocument.defaultView;
  const lineHeightPx = win ? parseFloat(win.getComputedStyle(el).lineHeight) || 0 : 0;
  const textLength = el.textContent?.length ?? 0;
  const lines = groupLines(collectTokens(el), lineHeightOf(el));
  const lineStarts = lines.map(line => line.charStart);
  if (lineStarts.length === 0) lineStarts.push(0);
  return { lineStarts, lineHeightPx, textLength };
};

export const measureParagraphLayout = (el: HTMLElement): ParagraphLayout => {
  const doc = el.ownerDocument;

  // Natural width of one space for a given font, measured once per font
  const spaceWidths = new Map<string, number>();
  const spaceWidth = (token: Token): number => {
    const cached = spaceWidths.get(token.fontKey);
    if (cached !== undefined) return cached;
    const probe = doc.createElement('span');
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.whiteSpace = 'pre';
    probe.style.fontStyle = token.font.style;
    probe.style.fontWeight = token.font.weight;
    probe.style.fontSize = token.font.size;
    probe.style.fontFamily = token.font.family;
    probe.style.letterSpacing = '0';
    probe.style.wordSpacing = '0';
    probe.textContent = ' ';
    doc.body.appendChild(probe);
    const width = probe.getBoundingClientRect().width;
    probe.remove();
    spaceWidths.set(token.fontKey, width);
    return width;
  };

  return groupLines(collectTokens(el), lineHeightOf(el)).map(({ tokens: lineTokens }) => {
    if (lineTokens.length === 0) return { runs: [], wordSpacingPt: 0 };

    // How much Chrome stretched each space to justify the line
    let naturalWidth = 0;
    let spaceCount = 0;
    for (const token of lineTokens) {
      if (token.kind === 'space') {
        naturalWidth += token.text.length * spaceWidth(token);
        spaceCount += token.text.length;
      } else {
        naturalWidth += token.right - token.left;
      }
    }
    const renderedWidth = lineTokens[lineTokens.length - 1].right - lineTokens[0].left;
    const extra = renderedWidth - naturalWidth;
    // Below half a pixel is measurement noise, not justification
    const wordSpacingPt = spaceCount > 0 && extra > 0.5 ? (extra / spaceCount) * PX_TO_PT : 0;

    const runs: LayoutRun[] = [];
    for (const token of lineTokens) {
      const last = runs[runs.length - 1];
      if (last && styleKey(last) === styleKey(token.style)) {
        last.text += token.text;
      } else {
        runs.push({ ...token.style, text: token.text });
      }
    }
    return { runs, wordSpacingPt };
  });
};
